import * as THREE from 'three';
import { fbm2, mulberry32 } from '../../core/rng';
import type { BuiltLocation, LocationDef } from '../Location';
import { paintableSnowMaterial, registerPaintable } from '../paintable';
import { box, collider, disposeScene, pine, rock, skyDome, stdMat, sun } from '../props';
import { createTerrain } from '../terrain';

/** Alpine slope with pines, rocks and a wooden cabin. Rolling inclined
 * terrain — the stress test for slope-correct paint projection. */
export const mountainLocation: LocationDef = {
  id: 'mountain',
  name: 'Mountain Slope',
  description: 'An alpine hillside in crisp daylight. Paint reads true even on the steep bits.',
  thumbnail: { skyTop: '#7ba6d9', skyBottom: '#dfeaf5', ground: '#f2f6fb', accent: '#2f5443' },

  build(ctx): BuiltLocation {
    const scene = new THREE.Scene();
    const colliders: THREE.Box3[] = [];
    const paintables: BuiltLocation['paintables'] = [];

    scene.fog = new THREE.FogExp2(0xdfe8f2, 0.006);
    scene.add(
      skyDome([
        [0, '#5f93cf'],
        [0.6, '#9dbfe2'],
        [1, '#e8f0f8'],
      ]),
    );

    scene.add(new THREE.HemisphereLight(0xbdd2ec, 0xe8ecf2, 0.85));
    scene.add(sun(0xfff2df, 1.6, 50, 80, 35, 85));

    const height = (x: number, z: number) =>
      -z * 0.16 +
      fbm2(x * 0.028, z * 0.028, ctx.seed) * 4.2 +
      fbm2(x * 0.11, z * 0.11, ctx.seed + 31) * 0.7;

    const snow = paintableSnowMaterial({ grainScale: 1400 });
    const terrain = createTerrain({ size: 170, segments: 130, height, material: snow.material });
    scene.add(terrain.mesh);
    registerPaintable(paintables, 'ground', terrain.mesh, snow.uniform, true);

    // Scattered pines and rocks (seeded, keeping the spawn clearing open).
    const rand = mulberry32(ctx.seed + 5);
    for (let i = 0; i < 46; i++) {
      const x = (rand() - 0.5) * 150;
      const z = (rand() - 0.5) * 150;
      if (Math.hypot(x, z - 14) < 14) continue;
      scene.add(pine(x, z, height(x, z) - 0.1, 0.9 + rand() * 1.5, colliders));
    }
    for (let i = 0; i < 12; i++) {
      const x = (rand() - 0.5) * 140;
      const z = (rand() - 0.5) * 140;
      if (Math.hypot(x, z - 14) < 12) continue;
      scene.add(rock(x, z, height(x, z), 0.5 + rand() * 1.3));
    }

    // Wooden cabin with a warm window.
    const cabin = new THREE.Group();
    const logMat = stdMat(0x7a5a40, 0.9);
    const cabinBody = box(6, 3, 4.6, logMat, 0, 1.5, 0);
    cabin.add(cabinBody);
    const roofMat = stdMat(0xe9eef6, 0.95);
    const roofL = box(3.6, 0.25, 5.4, roofMat, -1.55, 3.55, 0);
    roofL.rotation.z = 0.5;
    const roofR = box(3.6, 0.25, 5.4, roofMat, 1.55, 3.55, 0);
    roofR.rotation.z = -0.5;
    cabin.add(roofL, roofR);
    const door = box(1, 2, 0.1, stdMat(0x4a3626, 0.9), 1.4, 1, 2.32);
    cabin.add(door);
    const windowPane = new THREE.Mesh(
      new THREE.PlaneGeometry(1.1, 0.9),
      new THREE.MeshStandardMaterial({
        color: 0xffd9a0,
        emissive: 0xffb35c,
        emissiveIntensity: 1.4,
      }),
    );
    windowPane.position.set(-1.2, 1.7, 2.31);
    cabin.add(windowPane);
    const glowLight = new THREE.PointLight(0xffb35c, 32, 14, 2);
    glowLight.position.set(-1.2, 1.8, 3.2);
    cabin.add(glowLight);
    const cabinX = -16;
    const cabinZ = -4;
    cabin.position.set(cabinX, height(cabinX, cabinZ), cabinZ);
    scene.add(cabin);
    colliders.push(
      collider(cabinX, cabinZ, 6.4, 5, height(cabinX, cabinZ), height(cabinX, cabinZ) + 4.5),
    );

    return {
      scene,
      spawn: { pos: new THREE.Vector3(0, height(0, 14), 14), yaw: Math.PI },
      paintables,
      colliders,
      groundHeight: height,
      groundSurfaceIdAt: () => 'ground',
      bounds: { minX: -78, maxX: 78, minZ: -78, maxZ: 78 },
      baseFogDensity: 0.006,
      dispose() {
        disposeScene(scene);
      },
    };
  },
};
